import { Pagination } from 'react-bootstrap';
import {
  handleNextPageClick,
  handlePageClick,
  handlePreviousPageClick,
} from './pagination.helpers';
import { useLocation } from 'react-router-dom';

const PaginationItems = ({ className, totalPages, pageParam = 'page' }) => {
  const location = useLocation();
  const searchParams = Object.fromEntries(new URLSearchParams(location.search));
  const currentPage = searchParams[pageParam] ? Number(searchParams[pageParam]) : 1;

  // Generate page numbers with ellipsis for large page counts
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 10; // Max number of page buttons to show
    const surroundingPages = 3; // Pages to show on each side of current page

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate range around current page
      let startRange = Math.max(2, currentPage - surroundingPages);
      let endRange = Math.min(totalPages - 1, currentPage + surroundingPages);

      // Adjust range to show more pages when near the start or end
      const rangeSize = endRange - startRange + 1;
      const desiredRangeSize = maxVisiblePages - 2; // Minus first and last page

      if (rangeSize < desiredRangeSize) {
        if (currentPage < totalPages / 2) {
          // Near start - extend end range
          endRange = Math.min(totalPages - 1, startRange + desiredRangeSize - 1);
        } else {
          // Near end - extend start range
          startRange = Math.max(2, endRange - desiredRangeSize + 1);
        }
      }

      // Add ellipsis after first page if needed
      if (startRange > 2) {
        pages.push('ellipsis-start');
      }

      // Add pages around current page
      for (let i = startRange; i <= endRange; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endRange < totalPages - 1) {
        pages.push('ellipsis-end');
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <Pagination className={className}>
      <Pagination.First
        disabled={currentPage === 1}
        onClick={() => handlePageClick(1, pageParam)}
        title="First"
      />
      <Pagination.Prev
        disabled={currentPage === 1}
        onClick={() => handlePreviousPageClick(pageParam)}
      />
      {pageNumbers.map((page) => {
        if (page === 'ellipsis-start' || page === 'ellipsis-end') {
          return (
            <Pagination.Item key={page} disabled className="pagination-ellipsis">
              &hellip;
            </Pagination.Item>
          );
        }
        return (
          <Pagination.Item
            key={page}
            active={currentPage === page}
            activeLabel={null}
            onClick={() => handlePageClick(page, pageParam)}
          >
            {page}
          </Pagination.Item>
        );
      })}
      <Pagination.Next
        className="pagination-next"
        disabled={currentPage === totalPages}
        onClick={() => handleNextPageClick(totalPages, pageParam)}
      />
      <Pagination.Last
        className="pagination-next"
        disabled={currentPage === totalPages}
        onClick={() => handlePageClick(totalPages, pageParam)}
        title="Last"
      />
    </Pagination>
  );
};

export default PaginationItems;
